import { useEffect, type MouseEventHandler } from 'react';
import type React from 'react';

import { createPortal } from 'react-dom';
import { shallow } from 'zustand/shallow';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { inNextServer } from '@fabric-app/compat';
import useMobileSelectionStore from '@fabric-app/business/store/mobileSelection';
import { pick } from '@fabric-app/utils/object/manipulation';
import { FloatingFooter } from '@fabric-app/ui/components/FloatingFooter';
import useUIStore from '@/src/store/ui';
import { IconNavigational24Tag, IconSidePanelAssistant } from '@fabric-app/icons/svg';
import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { styled } from 'styled-components';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { SelectChevronDown } from '@fabric-app/ui/components/Select';
import { mediaHover } from '@fabric-app/ui/constants/styled-utils';
import plur from 'plur';
import Tooltip from '@fabric-app/ui/components/Tooltip';
import { useContextShareToken } from '@fabric-app/business/permission/contexts/AnonShareTokenContext';

const FOOTER = {
  key: 'selection-box',
  priority: 100,
};

const SelectionBox: React.FC<{
  onClickMove?: MouseEventHandler<HTMLButtonElement>;
  onClickDelete?: MouseEventHandler<HTMLButtonElement>;
  onClickCopy?: MouseEventHandler<HTMLButtonElement>;
  onClickEditTags?: MouseEventHandler<HTMLButtonElement>;
  onAskAssistantSelectionClick?: (e?: React.MouseEvent, added?: boolean) => void;
  itemsCount: number;
  onClickClearSelection: MouseEventHandler<HTMLButtonElement>;
  deleteText?: string;
  moveDisabledReason?: string;
  deleteDisabledReason?: string;
}> = ({
  onClickMove,
  onClickDelete,
  itemsCount,
  onClickCopy,
  onClickEditTags,
  onClickClearSelection,
  deleteText = 'Delete',
  onAskAssistantSelectionClick,
  moveDisabledReason,
  deleteDisabledReason,
}) => {
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  const { isMobileView } = useResponsive();
  const { disableMobileSelectMode } = useMobileSelectionStore(
    (state) => pick(state, ['mobileSelectModeIsActive', 'disableMobileSelectMode']),
    shallow,
  );

  const { floatingFooterActive, setFloatingFooter } = useUIStore(
    (state) => pick(state, ['floatingFooterActive', 'setFloatingFooter']),
    shallow,
  );

  const showSelectionBox = itemsCount > 0;

  useEffect(() => {
    setFloatingFooter(FOOTER, showSelectionBox);
  }, [showSelectionBox, setFloatingFooter]);

  const shouldRender = floatingFooterActive === FOOTER.key;

  const shareToken = useContextShareToken();

  if (!shouldRender) {
    return null;
  }

  const element = (
    <FloatingFooter.Container>
      {!isMobileView && (
        <FloatingFooter.Button
          key="cancel"
          onClick={onClickClearSelection}
          onMouseDown={(e) => {
            disableMobileSelectMode();
            stopPropagation(e);
          }}
          data-testid="selection-unselect-all-button"
        >
          Unselect all
        </FloatingFooter.Button>
      )}
      {!shareToken?.isPublishedView && (
        <>
          {onClickDelete && (
            <Tooltip
              label={deleteDisabledReason}
              placement="top"
              delay={0}
              disabled={!deleteDisabledReason}
            >
              <FloatingFooter.Button
                key="delete"
                onClick={onClickDelete}
                onMouseDown={stopPropagation}
                data-testid="selection-delete-button"
                variant="danger"
                disabled={!!deleteDisabledReason}
              >
                {deleteText}
              </FloatingFooter.Button>
            </Tooltip>
          )}

          {onClickMove && (
            <Tooltip
              label={moveDisabledReason}
              placement="top"
              delay={0}
              disabled={!moveDisabledReason}
            >
              <FloatingFooter.Button
                key="move"
                onClick={onClickMove}
                onMouseDown={stopPropagation}
                data-testid="selection-add-button"
                disabled={!!moveDisabledReason}
              >
                Move...
              </FloatingFooter.Button>
            </Tooltip>
          )}

          {onClickCopy && (
            <FloatingFooter.Button
              onMouseDown={stopPropagation}
              data-testid="selection-copy-button"
              onClick={onClickCopy}
            >
              Copy
            </FloatingFooter.Button>
          )}
          {onClickEditTags && (
            <FloatingFooter.Button
              onClick={onClickEditTags}
              onMouseDown={stopPropagation}
              data-testid="selection-tag-button"
              shape="square"
            >
              <IconNavigational24Tag />
            </FloatingFooter.Button>
          )}
        </>
      )}
      {onAskAssistantSelectionClick && (
        <DropdownMenu.Base
          key="ask-ai-dropdown"
          forcedTheme="invert"
          contentProps={{
            sideOffset: 10,
          }}
          renderTriggerElement={(props) => (
            <AskAiTrigger key="ask-ai" {...props} {...FloatingFooter.footerButtonMotionProps}>
              <IconSidePanelAssistant />
              Ask AI... <SelectChevronDown />
            </AskAiTrigger>
          )}
        >
          <DropdownMenu.Group>
            <DropdownMenu.Item onClick={(e) => onAskAssistantSelectionClick(e, false)}>
              Ask about {plur('item', itemsCount)}
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={(e) => onAskAssistantSelectionClick(e, true)}>
              Add to assistant context
            </DropdownMenu.Item>
          </DropdownMenu.Group>
        </DropdownMenu.Base>
      )}
    </FloatingFooter.Container>
  );

  return inNextServer() ? element : createPortal(element, document.body);
};

const AskAiTrigger = styled(DropdownMenu.Trigger)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  border-radius: 0.5rem;
  height: 2.5rem;
  border: 1px solid rgba(${cssVar['color-bg-primary-reverse-rgb']}, 0.4);
  color: ${cssVar['color-text-primary']};
  white-space: nowrap;
  padding: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  ${mediaHover} {
    &:hover {
      background-color: rgba(${cssVar['color-bg-primary-reverse-rgb']}, 0.08);
    }
  }
`;

export default SelectionBox;
